import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Switch,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  HStack,
} from '@wegroup/design-system';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as yup from 'yup';
import {
  boolean,
  dateMaxFiveYearsAgo,
} from '../../../../../../common/src/constants/validationSchemas';
import { Accident } from '../../../../../../../types/SMT';
import { DateInputV4 as DateInput } from 'wg-fe-ui';
import { format } from 'date-fns';

interface Props {
  accident?: Accident;
  isOpen: boolean;
  onClose: () => void;
  addAccident: (accident: Accident) => void;
  editAccident: (accident: Accident) => void;
}

const accidentValidationSchema = yup.object().shape({
  date: dateMaxFiveYearsAgo,
  at_fault: boolean.required,
});

const NewAccidentModal: React.FC<React.PropsWithChildren<Props>> = ({
  accident,
  addAccident,
  editAccident,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const modalGoal = accident ? 'EDIT' : 'ADD';

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<Accident>({
    resolver: yupResolver(accidentValidationSchema),
    defaultValues: {
      ...(accident?.date && {
        date: accident?.date,
      }),
      at_fault: accident?.at_fault || false,
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = handleSubmit((newAccident: Accident) => {
    if (accident)
      editAccident({
        date: format(
          new Date(newAccident.date),
          `yyyy-MM-dd'T'HH:mm:ss.SSSxxx`,
        ),
        at_fault: newAccident.at_fault,
      });
    else
      addAccident({
        date: format(
          new Date(newAccident.date),
          `yyyy-MM-dd'T'HH:mm:ss.SSSxxx`,
        ),
        at_fault: newAccident.at_fault,
      });
    handleOnClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`FLOWS_COMPANY-SCAN_ACCIDENT-INPUT_${modalGoal}-ACCIDENT`)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={VStack} spacing="4">
          <FormControl isInvalid={!!errors.date}>
            <FormLabel>{t('Date of the accident')}</FormLabel>
            <Controller
              name="date"
              control={control}
              render={({ field: { onChange, value } }) => (
                <StyledDateInput
                  error={errors.date?.message}
                  onChange={({ value }: { value: Date }) => onChange(value)}
                  value={value}
                  data-test-id="ProdFac_AccidentForm_AccidentDate"
                />
              )}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.at_fault}>
            <FormLabel>{t('At fault')}</FormLabel>
            <Controller
              name="at_fault"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Switch
                  name={name}
                  isChecked={value}
                  onChange={onChange}
                  checked={value}
                  data-test-id="ProdFac_AccidentForm_AtFault"
                />
              )}
            />
            <FormErrorMessage>{errors.at_fault?.message}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter as={HStack} justify="flex-end" spacing="4">
          <Button variant="ghost" mr={3} onClick={handleOnClose}>
            {t('CLOSE')}
          </Button>
          <Button onClick={handleOnSubmit}>
            {modalGoal ? t('SAVE') : t('ADD')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const StyledDateInput = styled(DateInput)`
  height: unset;
  > div {
    /* ErrorContainer */
    height: unset;
  }
`;

export default NewAccidentModal;
